import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge, Stat } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  PenLine, Plus, Send, Download, ExternalLink, Shield, Trash2, Users,
  Clock, CheckCircle2, X, Copy, Mail, Loader2,
} from "lucide-react";

const STATUS_TONE = {
  draft: "neutral",
  sent: "warn",
  in_progress: "warn",
  completed: "success",
  voided: "danger",
};

const STATUS_LABEL = {
  draft: "Draft",
  sent: "Sent",
  in_progress: "In progress",
  completed: "Completed",
  voided: "Voided",
};

export default function SignaturesPage() {
  const [envelopes, setEnvelopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showNew, setShowNew] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/envelopes").then((r) => setEnvelopes(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!filter) return envelopes;
    return envelopes.filter((e) => e.status === filter);
  }, [envelopes, filter]);

  const stats = useMemo(() => ({
    total: envelopes.length,
    draft: envelopes.filter((e) => e.status === "draft").length,
    out: envelopes.filter((e) => ["sent", "in_progress"].includes(e.status)).length,
    completed: envelopes.filter((e) => e.status === "completed").length,
  }), [envelopes]);

  return (
    <div data-testid="signatures-page">
      <PageHeader
        eyebrow="Draft · Agree · Sign"
        title="Signatures"
        description="Send any document for electronic signature. Multi-signer order, witnesses, SHA-256 integrity, full audit trail — no envelope fee, no separate account."
        action={
          <Button
            onClick={() => setShowNew(true)}
            className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
            data-testid="new-envelope-btn"
          >
            <Plus size={14} className="mr-2" /> New envelope
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="All envelopes" value={stats.total} testId="stat-total" />
        <Stat label="Drafts" value={stats.draft} testId="stat-draft" />
        <Stat label="Out for signature" value={stats.out} testId="stat-out" />
        <Stat label="Completed" value={stats.completed} testId="stat-completed" />
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-5">
        <span className="label-eyebrow mr-1">Filter</span>
        {["", "draft", "sent", "in_progress", "completed", "voided"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border ${filter === s ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0] hover:border-[#7B56C9]"}`}
            data-testid={`filter-${s || "all"}`}
          >
            {s === "" ? "All" : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#4A5568] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading envelopes…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={PenLine}
          title={envelopes.length ? "No envelopes match this filter" : "You have no envelopes yet"}
          description="Create a new envelope, add one or more signers, send it, and sign securely in the browser. Every action is logged and hashed."
          action={
            <Button
              onClick={() => setShowNew(true)}
              className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
              data-testid="empty-new-envelope-btn"
            >
              <Plus size={14} className="mr-2" /> New envelope
            </Button>
          }
        />
      ) : (
        <div className="border border-[#E8E3F0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.18em] uppercase text-[#4A5568] border-b border-[#E8E3F0]">
                <th className="text-left px-4 py-3 font-normal">Title</th>
                <th className="text-left px-4 py-3 font-normal">Signers</th>
                <th className="text-left px-4 py-3 font-normal">Status</th>
                <th className="text-left px-4 py-3 font-normal">Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr
                  key={e.id}
                  className="border-b border-[#E8E3F0] last:border-0 hover:bg-[#FAFAF8] cursor-pointer"
                  onClick={() => navigate(`/app/signatures/${e.id}`)}
                  data-testid={`envelope-row-${e.id}`}
                >
                  <td className="px-4 py-3 font-serif text-base">{e.title}</td>
                  <td className="px-4 py-3 text-[#4A5568]">
                    {e.signers?.length || 0} ·{" "}
                    <span className="text-[11px]">
                      {e.signers?.filter((s) => s.status === "signed").length || 0} signed
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge tone={STATUS_TONE[e.status] || "neutral"}>
                      {STATUS_LABEL[e.status] || e.status}
                    </StatusBadge>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#4A5568]">
                    {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ExternalLink size={14} className="text-[#4A5568]" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <NewEnvelopeModal
          onClose={() => setShowNew(false)}
          onCreated={(env) => {
            setShowNew(false);
            navigate(`/app/signatures/${env.id}`);
          }}
        />
      )}
    </div>
  );
}

// --- New Envelope modal -----------------------------------------------------
function NewEnvelopeModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [message, setMessage] = useState("");
  const [signers, setSigners] = useState([
    { name: "", email: "", role: "signer", order: 1 },
  ]);
  const [ordered, setOrdered] = useState(true);
  const [agreementId, setAgreementId] = useState("");
  const [agreements, setAgreements] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/agreements").then((r) => setAgreements(r.data || []));
  }, []);

  const addSigner = () =>
    setSigners((s) => [...s, { name: "", email: "", role: "signer", order: s.length + 1 }]);
  const removeSigner = (i) =>
    setSigners((s) => s.filter((_, idx) => idx !== i).map((x, idx) => ({ ...x, order: idx + 1 })));
  const update = (i, patch) =>
    setSigners((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const loadFromAgreement = (id) => {
    setAgreementId(id);
    if (!id) return;
    const ag = agreements.find((a) => a.id === id);
    if (!ag) return;
    if (!title) setTitle(ag.title || "");
    if (!content) setContent(ag.content || "");
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Title is required");
    if (!content.trim()) return toast.error("Document body is required");
    if (signers.some((s) => !s.name || !s.email))
      return toast.error("Every signer needs a name and an email");
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        content,
        message: message || null,
        signers: signers.map((s, i) => ({
          name: s.name.trim(),
          email: s.email.trim(),
          role: s.role,
          order: ordered ? i + 1 : 1,
        })),
        ordered,
        source_agreement_id: agreementId || null,
      };
      const r = await api.post("/envelopes", payload);
      toast.success("Envelope created");
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create envelope");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0B132B]/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="new-envelope-modal">
      <div className="bg-white max-w-3xl w-full mt-10 mb-10 border border-[#E8E3F0]">
        <div className="px-6 py-4 border-b border-[#E8E3F0] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">La Signature</div>
            <h2 className="font-serif text-2xl">New envelope</h2>
          </div>
          <button onClick={onClose} className="text-[#4A5568] hover:text-[#2A0F5A]" data-testid="close-new-envelope">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {agreements.length > 0 && (
            <div>
              <Label className="label-eyebrow mb-1.5 block">Pre-fill from an agreement</Label>
              <select
                value={agreementId}
                onChange={(e) => loadFromAgreement(e.target.value)}
                className="w-full border border-[#E8E3F0] px-3 py-2 text-sm bg-white"
                data-testid="agreement-source-select"
              >
                <option value="">— None —</option>
                {agreements.map((a) => (
                  <option key={a.id} value={a.id}>{a.title}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label className="label-eyebrow mb-1.5 block">Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. SaaS Subscription Agreement — Acme Inc."
              className="rounded-none border-[#E8E3F0]"
              data-testid="env-title-input"
            />
          </div>

          <div>
            <Label className="label-eyebrow mb-1.5 block">Document body *</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste or write the full text that signers will see and sign."
              rows={10}
              className="rounded-none border-[#E8E3F0] font-mono text-[13px] leading-relaxed"
              data-testid="env-content-input"
            />
          </div>

          <div>
            <Label className="label-eyebrow mb-1.5 block">Message to signers (optional)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="A short note the recipients will see when they open the link."
              rows={2}
              className="rounded-none border-[#E8E3F0]"
              data-testid="env-message-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="label-eyebrow mb-0">Signers</Label>
              <label className="text-xs flex items-center gap-2 text-[#4A5568]">
                <input
                  type="checkbox"
                  checked={ordered}
                  onChange={(e) => setOrdered(e.target.checked)}
                  data-testid="env-ordered-toggle"
                />
                Signing order matters
              </label>
            </div>
            <div className="space-y-3">
              {signers.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`signer-row-${i}`}>
                  <div className="col-span-1 label-eyebrow text-center">{i + 1}</div>
                  <Input
                    value={s.name}
                    onChange={(e) => update(i, { name: e.target.value })}
                    placeholder="Full name"
                    className="col-span-4 rounded-none border-[#E8E3F0]"
                    data-testid={`signer-name-${i}`}
                  />
                  <Input
                    value={s.email}
                    onChange={(e) => update(i, { email: e.target.value })}
                    placeholder="email@example.com"
                    className="col-span-4 rounded-none border-[#E8E3F0]"
                    data-testid={`signer-email-${i}`}
                  />
                  <select
                    value={s.role}
                    onChange={(e) => update(i, { role: e.target.value })}
                    className="col-span-2 border border-[#E8E3F0] px-2 py-2 text-xs bg-white"
                    data-testid={`signer-role-${i}`}
                  >
                    <option value="signer">Signer</option>
                    <option value="witness">Witness</option>
                  </select>
                  <button
                    onClick={() => removeSigner(i)}
                    disabled={signers.length <= 1}
                    className="col-span-1 text-[#4A5568] hover:text-[#8a2121] disabled:opacity-30"
                    data-testid={`signer-remove-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button
                onClick={addSigner}
                className="text-xs text-[#2A0F5A] hover:underline flex items-center gap-1"
                data-testid="add-signer-btn"
              >
                <Plus size={12} /> Add signer
              </button>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E8E3F0] flex items-center justify-end gap-3 bg-[#FAFAF8]">
          <Button variant="outline" onClick={onClose} className="rounded-none border-[#E8E3F0]" data-testid="cancel-new-env">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
            data-testid="submit-new-env"
          >
            {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Creating…</> : "Create draft"}
          </Button>
        </div>
      </div>
    </div>
  );
}
